import Decimal from 'decimal.js'
import { isEmpty, isUndefined } from 'lodash-es'
import {
    GiftCardDetailContract,
    PaymentContract,
    PaymentSettingContract,
    PaymentStatus,
} from 'models'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { showErrorMessage } from 'store/actions/ui.action'
import { sumDecimal } from 'utilities/decimalsManipulate'
import { RECEIVE_PAYMENT_STATUS } from './SplitPayment'
import { PaymentFormModel } from './SplitPaymentContext'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { IPaymentTypeSelection } from '../../hook/usePaymentContext'
const NEAREST_NUMBER = 0.01
const isUnpaidInput = (i: PaymentFormModel) => isUndefined(i.PaymentID)
const isManualInput = (i: PaymentFormModel) => isUnpaidInput(i) && i.isManual
const isAutoInput = (i: PaymentFormModel) => isUnpaidInput(i) && !i.isManual
interface props {
    goToSinglePayment: () => void

    //from payment context
    paidPayments: PaymentContract[]
    onSetExpectAmount: (amount: Decimal, paymentType?: number) => void
    onSelectPaymentType: (paymentSettingID: string) => void
    orderTotalDue: Decimal
    defaultPaymentType: PaymentSettingContract | undefined
    onChangePaymentForms: (forms: PaymentFormModel[]) => void
    paymentForms: PaymentFormModel[]
    selectedGiftCard: GiftCardDetailContract | undefined
    onSelectGiftCard: (giftCard: GiftCardDetailContract | undefined) => void

    addTipsMode: boolean
    onExitAddTipsMode: () => void
    paymentTypeLists: IPaymentTypeSelection[]
}
export const useSplitPayment = ({
    goToSinglePayment,
    onSetExpectAmount,
    onSelectPaymentType,
    orderTotalDue,
    defaultPaymentType,
    paidPayments,
    onChangePaymentForms,
    paymentForms,
    selectedGiftCard,
    onSelectGiftCard,
    addTipsMode,
    onExitAddTipsMode,
    paymentTypeLists,
}: props) => {
    const dispatch = useDispatch()

    const [isCharging, setIsCharging] = useState(false)
    const [chargingPaymentIndex, setChargingPaymentIndex] = useState<number>(0)
    const { t } = useTranslation()
    const adjustExpectAmount = useCallback(
        (payments: PaymentFormModel[], expectAmount: Decimal) => {
            const total = sumDecimal(
                payments.filter(isAutoInput).map((a) => a.expectAmount),
            )

            const diff = expectAmount.minus(total)

            if (!diff.equals(new Decimal(0))) {
                let lastUnChargePayment = payments.length - 1
                payments.forEach((a, i) => {
                    if (isAutoInput(a)) lastUnChargePayment = i
                })

                payments[lastUnChargePayment].expectAmount = payments[
                    lastUnChargePayment
                ].expectAmount
                    .add(diff)
                    .lessThan(new Decimal(0))
                    ? new Decimal(0)
                    : payments[lastUnChargePayment].expectAmount.add(diff)
            }
            return payments
        },
        [],
    )
    const defaultPayments = useCallback(
        (
            selectedPaymentType: PaymentSettingContract | undefined,
            orderTotalDue: Decimal,
        ) => {
            return adjustExpectAmount(
                [
                    {
                        paymentType: selectedPaymentType?.paymentTypeID,
                        paymentSettingID: selectedPaymentType?.paymentSettingID!,
                        expectAmount: orderTotalDue
                            .dividedBy(2)
                            .toNearest(NEAREST_NUMBER),
                        isManual: false,
                    },
                    {
                        paymentType: selectedPaymentType?.paymentTypeID,
                        paymentSettingID: selectedPaymentType?.paymentSettingID!,
                        expectAmount: orderTotalDue
                            .dividedBy(2)
                            .toNearest(NEAREST_NUMBER),
                        isManual: false,
                    },
                ],
                orderTotalDue,
            )
        },
        [adjustExpectAmount],
    )

    const paymentWithSelectedGiftCard = useCallback(
        (
            selectedGiftCard: GiftCardDetailContract,
            selectedPaymentType: PaymentSettingContract | undefined,
            orderTotalDue: Decimal,
        ): PaymentFormModel[] => {
            const amountLeft = orderTotalDue.minus(
                new Decimal(selectedGiftCard?.currentBalance ?? 0),
            )

            return adjustExpectAmount(
                [
                    {
                        paymentType: PAYMENT_TYPE_ID.GIFTCARD,
                        paymentSettingID: paymentTypeLists.find(
                            (a) =>
                                a.paymentTypeID === PAYMENT_TYPE_ID.GIFTCARD &&
                                !a.customPaymentTypeID,
                        )?.paymentSettingID!,
                        giftcard: selectedGiftCard,
                        expectAmount: new Decimal(
                            selectedGiftCard?.currentBalance ?? 0,
                        ),
                        isManual: true,
                    },

                    {
                        paymentType: selectedPaymentType?.paymentTypeID,
                        paymentSettingID: selectedPaymentType?.paymentSettingID!,
                        expectAmount: amountLeft,
                        isManual: false,
                    },
                ],
                orderTotalDue,
            )
        },
        [adjustExpectAmount, paymentTypeLists],
    )

    const paymentWithPaidPayment = useCallback(
        (
            paidPayments: PaymentContract[],
            orderTotalDue: Decimal,
            selectedPaymentType: PaymentSettingContract | undefined,
        ) => {
            let payments = paidPayments
                .filter((p) => RECEIVE_PAYMENT_STATUS.includes(p.status!))
                .map<PaymentFormModel>((p) => {
                    const returned = new Decimal(
                        paidPayments.find(
                            (c) =>
                                c.status === PaymentStatus.Returned &&
                                c.referencePaymentID === p.paymentID,
                        )?.amount ?? 0,
                    )

                    return {
                        paymentType: p.paymentTypeID!,
                        paymentSettingID: paymentTypeLists.find(
                            (a) =>
                                a.paymentTypeID === p.paymentTypeID &&
                                a.customPaymentTypeID === p.customPaymentTypeID,
                        )?.paymentSettingID!,
                        expectAmount: new Decimal(p.amount ?? 0).minus(
                            returned,
                        ),
                        PaymentID: p.paymentID!,
                        isManual: false,
                    }
                })
            const remaining = orderTotalDue.minus(
                sumDecimal(payments.map((p) => p.expectAmount)),
            )
            payments.push({
                paymentType: selectedPaymentType?.paymentTypeID,
                paymentSettingID: selectedPaymentType?.paymentSettingID!,
                expectAmount: remaining,
                isManual: false,
            })
            return payments
        },
        [paymentTypeLists],
    )
    const initPayments = useCallback(() => {
        if (paidPayments.length > 0) {
            /**already has paid payment */
            return paymentWithPaidPayment(
                paidPayments,
                orderTotalDue,
                defaultPaymentType,
            )
        } else if (selectedGiftCard && selectedGiftCard?.currentBalance !== 0) {
            return paymentWithSelectedGiftCard(
                selectedGiftCard,
                defaultPaymentType,
                orderTotalDue,
            )
        } else {
            return defaultPayments(defaultPaymentType, orderTotalDue)
        }
    }, [
        defaultPayments,
        orderTotalDue,
        paidPayments,
        paymentWithPaidPayment,
        paymentWithSelectedGiftCard,
        selectedGiftCard,
        defaultPaymentType,
    ])

    const remainingAmount = useMemo(() => {
        return orderTotalDue.minus(
            sumDecimal(
                (paymentForms.filter((a) => !isUnpaidInput(a)) ?? []).map(
                    (o) => o.expectAmount,
                ),
            ),
        )
    }, [orderTotalDue, paymentForms])

    const paymentCount = useMemo(() => paymentForms.length, [paymentForms])
    const reCalculateAmount = useCallback(
        (payments: PaymentFormModel[]) => {
            const remainingValueForReCalculated = remainingAmount.minus(
                sumDecimal(
                    (payments.filter(isManualInput) ?? []).map(
                        (o) => o.expectAmount,
                    ),
                ),
            )

            const autoInputCount = payments.filter(isAutoInput).length

            if (autoInputCount > 0) {
                payments.forEach((payment) => {
                    if (isAutoInput(payment)) {
                        payment.expectAmount = remainingValueForReCalculated
                            .dividedBy(autoInputCount)
                            .toNearest(NEAREST_NUMBER)
                            .lessThanOrEqualTo(new Decimal(0))
                            ? new Decimal(0)
                            : remainingValueForReCalculated
                                  .dividedBy(autoInputCount)
                                  .toNearest(NEAREST_NUMBER)
                    }
                })

                return adjustExpectAmount(
                    [...payments],
                    remainingValueForReCalculated,
                )
            } else return payments
        },
        [adjustExpectAmount, remainingAmount],
    )
    const onCharge = useCallback(
        (payment: PaymentFormModel, index: number) => {
            if (!payment.paymentType)
                dispatch(
                    showErrorMessage(t('finish.message.payment_type_require')),
                )
            else if (remainingAmount.lessThan(payment.expectAmount))
                dispatch(
                    showErrorMessage(
                        t('finish.message.unable_pay_more_remaining'),
                    ),
                )
            else {
                onSelectPaymentType(payment.paymentSettingID!)
                onSetExpectAmount(payment.expectAmount, payment.paymentType!)
                onSelectGiftCard(payment.giftcard)
                setChargingPaymentIndex(index)
                setIsCharging(true)
            }
        },
        [
            dispatch,
            onSelectGiftCard,
            onSelectPaymentType,
            onSetExpectAmount,
            remainingAmount,
            t,
        ],
    )
    const addPayment = useCallback(() => {
        onChangePaymentForms([
            ...reCalculateAmount([
                ...paymentForms,
                {
                    paymentType: defaultPaymentType?.paymentTypeID!,
                    paymentSettingID: defaultPaymentType?.paymentSettingID!,
                    expectAmount: new Decimal(0),
                    isManual: false,
                },
            ]),
        ])
    }, [
        onChangePaymentForms,
        reCalculateAmount,
        paymentForms,
        defaultPaymentType,
    ])
    const onChangePayment = useCallback(
        (payment: PaymentFormModel, index: number) => {
            const newPaymentForms = paymentForms.map((a, i) => {
                if (i === index) return payment
                return a
            })

            onChangePaymentForms(reCalculateAmount(newPaymentForms))
        },
        [paymentForms, onChangePaymentForms, reCalculateAmount],
    )
    const onFinishCharging = useCallback(
        (paymentID: string | undefined) => {
            onChangePaymentForms(
                paymentForms.map((a, i) => {
                    if (i === chargingPaymentIndex) a.PaymentID = paymentID
                    return a
                }),
            )
            setIsCharging(false)
        },
        [chargingPaymentIndex, onChangePaymentForms, paymentForms],
    )
    const deletePayment = useCallback(
        (index: number) => {
            let newPayments = [...paymentForms]
            newPayments.splice(index, 1)

            newPayments = reCalculateAmount(newPayments)
            onChangePaymentForms(newPayments)
        },
        [paymentForms, reCalculateAmount, onChangePaymentForms],
    )

    const reducePayment = useCallback(() => {
        let newPayments = [...paymentForms]
        let lastUnpaidIndex = -1
        for (let i = newPayments.length - 1; i > -1; i--) {
            if (isUnpaidInput(newPayments[i])) {
                lastUnpaidIndex = i
                break
            }
        }
        if (lastUnpaidIndex === -1) return
        newPayments.splice(lastUnpaidIndex, 1)
        newPayments = reCalculateAmount(newPayments)

        onChangePaymentForms(newPayments)
    }, [paymentForms, reCalculateAmount, onChangePaymentForms])
    const splitEvenly = useCallback(() => {
        onChangePaymentForms(
            reCalculateAmount(
                paymentForms.map((i) => {
                    return { ...i, isManual: false }
                }),
            ),
        )
    }, [onChangePaymentForms, reCalculateAmount, paymentForms])

    const onClickBack = useCallback(() => {
        if (addTipsMode) onExitAddTipsMode()
        else if (isCharging) setIsCharging(false)
        else {
            goToSinglePayment()
            onSelectGiftCard(undefined)
            onSelectPaymentType(defaultPaymentType?.paymentSettingID!)
        }
    }, [
        addTipsMode,
        defaultPaymentType,
        goToSinglePayment,
        isCharging,
        onExitAddTipsMode,
        onSelectGiftCard,
        onSelectPaymentType,
    ])

    useEffect(() => {
        if (isEmpty(paymentForms)) onChangePaymentForms(initPayments())
    }, [paymentForms, onChangePaymentForms, initPayments])

    return {
        isCharging,
        onClickCharging: onCharge,
        onChangePayment,
        reducePayment,
        deletePayment,
        addPayment,
        paymentCount,
        remainingAmount,
        splitEvenly,
        onFinishCharging,
        onClickBack,
    }
}
